import React, { useEffect, useMemo } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import HighlightedText from "components/Shared/HighlightedText";
import { useTablePagination, useAppMeta } from "hooks";
import { useCustomParams } from "hooks/useCustomParams";
import { useLazyListGLSalesReportQuery } from "store/services/generalLedgerApi";
import GLSalesReportFilter from "./GLSalesReportFilter";

const formatSalesAmount = (amount) => {
    const value = Number(amount || 0);
    const isPositive = value >= 0;
    const sign = isPositive ? "+" : "-";
    return `${sign} Rs. ${Math.abs(value).toFixed(2)}`;
};

const GeneralLedgerSalesReport = () => {
    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();
    const [searchParams] = useSearchParams();
    const { params } = useCustomParams();

    const keyword = searchParams.get("search") || "";
    const fromDate = params.fromDate || "";
    const toDate = params.toDate || "";

    const [
        fetchGLSalesReport,
        { data: glRes, isLoading, isFetching, error },
    ] = useLazyListGLSalesReportQuery();

    useEffect(() => {
        fetchGLSalesReport({
            page: currentPage,
            limit: perPage,
            ...(keyword && { keyword }),
            ...(fromDate && { fromDate }),
            ...(toDate && { toDate }),
            ...(params.serviceChargePayer && {
                serviceChargePayer: params.serviceChargePayer,
            }),
            ...(params.sourceBranch && {
                sourceBranch: params.sourceBranch,
            }),
            ...(params.destinationBranch && {
                destinationBranch: params.destinationBranch,
            }),
            ...(params.sourceRegion && {
                sourceProvince: params.sourceRegion,
            }),
            ...(params.destinationRegion && {
                destinationProvince: params.destinationRegion,
            }),
            ...(params.feeItem && { feeItem: params.feeItem }),
        });
    }, [
        currentPage,
        perPage,
        keyword,
        fromDate,
        toDate,
        params.serviceChargePayer,
        params.sourceBranch,
        params.destinationBranch,
        params.sourceRegion,
        params.destinationRegion,
        params.feeItem,
        fetchGLSalesReport,
    ]);

    useAppMeta("GL Sales Report");

    const columns = useMemo(
        () => [
            {
                name: "GL No",
                selector: (row) => row.glNumber || "N/A",
                cell: (row) => (
                    <HighlightedText text={row.glNumber || "N/A"} highlight={keyword} />
                ),
            },
            {
                name: "Shipment ID",
                selector: (row) => row.shipment?.shipmentId || "N/A",
                cell: (row) =>
                    row.shipment?.shipmentId ? (
                        <HighlightedText
                            text={row.shipment.shipmentId}
                            highlight={keyword}
                        />
                    ) : (
                        "N/A"
                    ),
            },
            {
                name: "Source Branch",
                selector: (row) => row.shipment?.sourceBranch?.name || "N/A",
                cell: (row) => (
                    <Box
                        sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                        title={row.shipment?.sourceBranch?.name || ""}
                    >
                        <div>{row.shipment?.sourceBranch?.name || "N/A"}</div>
                        {row.shipment?.sourceBranch?.region?.name && (
                            <Typography variant="caption" color="text.secondary">
                                {row.shipment.sourceBranch.region.name}
                            </Typography>
                        )}
                    </Box>
                ),
            },
            {
                name: "Destination Branch",
                selector: (row) => row.shipment?.destinationBranch?.name || "N/A",
                cell: (row) => (
                    <Box
                        sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                        title={row.shipment?.destinationBranch?.name || ""}
                    >
                        <div>{row.shipment?.destinationBranch?.name || "N/A"}</div>
                        {row.shipment?.destinationBranch?.region?.name && (
                            <Typography variant="caption" color="text.secondary">
                                {row.shipment.destinationBranch.region.name}
                            </Typography>
                        )}
                    </Box>
                ),
            },
            {
                name: "Event",
                selector: (row) => row.events || "N/A",
                cell: (row) => (
                    <Box
                        sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                        title={row.events || ""}
                    >
                        {row.events || "N/A"}
                    </Box>
                ),
            },
            {
                name: "Sales Amount",
                selector: (row) => row.salesAmount,
                cell: (row) => {
                    const credit = Number(row.salesCredit || 0);
                    const debit = Number(row.salesDebit || 0);
                    const value = credit - debit;
                    const isPositive = value >= 0;
                    return (
                        <Chip
                            label={formatSalesAmount(value)}
                            size="small"
                            sx={{
                                backgroundColor: isPositive ? "#D4EDDA" : "#F8D7DA",
                                color: isPositive ? "#155724" : "#721C24",
                                fontWeight: "bold",
                                height: "22px",
                                borderRadius: "8px",
                                "& .MuiChip-label": {
                                    fontSize: "0.75rem",
                                    padding: "0 8px",
                                },
                            }}
                        />
                    );
                },
                right: true,
            },
            {
                name: "Created At",
                selector: (row) => <AppTableDateTime date={row?.createdAt} />,
            },
            {
                name: "Shipment Date",
                selector: (row) =>
                    row?.shipment?.createdAt ? (
                        <AppTableDateTime date={row.shipment.createdAt} />
                    ) : (
                        "N/A"
                    ),
            },
        ],
        [keyword],
    );

    return (
        <BreadcrumbLayout
            breadcrumbs={[
                { title: "Reports", path: "/app/crm/reports/list" },
                { title: "GL Sales Report" },
            ]}
        >
            <BreadcrumbLayout.Error error={error} />
            <BreadcrumbLayout.Paper>
                <GLSalesReportFilter disabled={isLoading || isFetching} />

                <Box sx={{ px: 2, py: 1 }}>
                    <Stack
                        direction="row"
                        spacing={4}
                        alignItems="center"
                        justifyContent="flex-end"
                        sx={{ flexWrap: "wrap", gap: 2 }}
                    >
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Total Credit Sales (+)
                            </Typography>
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                sx={{ color: "#155724" }}
                            >
                                + Rs. {(glRes?.totalCreditSales || 0).toFixed(2)}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Total Debit Sales (−)
                            </Typography>
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                sx={{ color: "#721C24" }}
                            >
                                − Rs. {(glRes?.totalDebitSales || 0).toFixed(2)}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Net Sales
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="primary">
                                {formatSalesAmount(glRes?.totalSales || 0)}
                            </Typography>
                        </Box>
                    </Stack>
                </Box>

                <AppTable
                    columns={columns}
                    data={glRes?.data ?? []}
                    progressPending={isLoading || isFetching}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={glRes?.totalItems ?? 0}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                />
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default GeneralLedgerSalesReport;
